class CollectionSort extends HTMLElement {
  connectedCallback() {
    this.nativeSelect = this.querySelector('select[name="sort_by"]');
    this.combobox     = this.querySelector('[data-sort-combobox]');
    this.listbox      = this.querySelector('[role="listbox"]');
    this.label        = this.querySelector('[data-sort-label]');
    this.options      = Array.from(this.querySelectorAll('[role="option"]'));

    if (!this.combobox || !this.listbox) return;

    this.activeIndex  = Math.max(0, this.options.findIndex(o => o.getAttribute('aria-selected') === 'true'));
    this.searchString = '';
    this.searchTimer  = null;

    this.combobox.addEventListener('click', () => this.toggle());
    this.combobox.addEventListener('keydown', e => this.onKeyDown(e));
    this.combobox.addEventListener('blur', () => {
      requestAnimationFrame(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    });

    this.listbox.addEventListener('mousedown', e => e.preventDefault());
    this.listbox.addEventListener('click', e => {
      const index = this.options.indexOf(e.target.closest('[role="option"]'));
      if (index !== -1) this.selectOption(index);
    });
    this.listbox.addEventListener('pointermove', e => {
      const index = this.options.indexOf(e.target.closest('[role="option"]'));
      if (index !== -1 && index !== this.activeIndex) this.setActive(index, false);
    });

    this.onDocPointerdown = e => {
      if (!this.contains(e.target) && this.isOpen) this.close();
    };
    document.addEventListener('pointerdown', this.onDocPointerdown);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onDocPointerdown);
  }

  get isOpen() {
    return this.combobox.getAttribute('aria-expanded') === 'true';
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.listbox.removeAttribute('hidden');
    this.combobox.setAttribute('aria-expanded', 'true');
    this.setActive(this.activeIndex, false);
  }

  close() {
    this.listbox.setAttribute('hidden', '');
    this.combobox.setAttribute('aria-expanded', 'false');
    this.combobox.removeAttribute('aria-activedescendant');
  }

  setActive(index, scroll = true) {
    if (index < 0 || index >= this.options.length) return;
    this.activeIndex = index;
    const opt = this.options[index];
    this.options.forEach(o => o.classList.toggle('sort-option--active', o === opt));
    this.combobox.setAttribute('aria-activedescendant', opt.id);
    if (scroll) opt.scrollIntoView({ block: 'nearest' });
  }

  selectOption(index) {
    const opt = this.options[index];
    if (!opt) return;
    this.options.forEach(o => o.setAttribute('aria-selected', String(o === opt)));
    if (this.label) this.label.textContent = opt.textContent.trim();
    if (this.nativeSelect) this.nativeSelect.value = opt.dataset.value;
    this.close();
    this.dispatchEvent(new CustomEvent('collection:sort', { bubbles: true }));
  }

  onKeyDown(e) {
    const { key } = e;
    const last = this.options.length - 1;

    if (!this.isOpen) {
      const opens = ['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
      if (opens.includes(key) || (key.length === 1 && !e.metaKey && !e.ctrlKey)) {
        e.preventDefault();
        this.open();
        if (key.length === 1) this.typeAhead(key);
      }
      return;
    }

    const nav = { ArrowDown: 1, ArrowUp: -1, PageDown: 10, PageUp: -10 };
    if (key in nav) {
      e.preventDefault();
      this.setActive(Math.min(Math.max(this.activeIndex + nav[key], 0), last));
      return;
    }

    switch (key) {
      case 'Home':
        e.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        e.preventDefault();
        this.setActive(last);
        break;
      case 'Enter': case ' ': case 'Tab':
        e.preventDefault();
        this.selectOption(this.activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        this.close();
        this.combobox.focus();
        break;
      default:
        if (key.length === 1 && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          this.typeAhead(key);
        }
    }
  }

  typeAhead(char) {
    clearTimeout(this.searchTimer);
    this.searchString += char.toLowerCase();
    this.searchTimer = setTimeout(() => { this.searchString = ''; }, 500);

    const start = this.isOpen ? this.activeIndex + 1 : 0;
    const rotated = [...this.options.slice(start), ...this.options.slice(0, start)];
    const match = rotated.find(o => o.textContent.trim().toLowerCase().startsWith(this.searchString));
    if (match) this.setActive(this.options.indexOf(match));
  }
}

if (!customElements.get('collection-sort')) customElements.define('collection-sort', CollectionSort);
